import { useEffect, useMemo, useState } from 'react';

type LibraryItem = {
  id: number;
  source: string;
  kind: 'chapter' | 'appendix' | 'full' | string;
  ord: string;
  ord_num: number | null;
  title: string;
  file_name: string;
  size: number | null;
  source_url: string | null;
};

const fmtSize = (n: number | null) =>
  !n ? '' : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;

export function Library() {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    fetch('/api/library?source=slp3')
      .then((r) => r.json())
      .then(setItems);
  }, []);

  const groups = useMemo(() => {
    const filter = q.trim().toLowerCase();
    const filtered = filter
      ? items.filter(
          (i) =>
            i.title.toLowerCase().includes(filter) ||
            i.ord.toLowerCase().includes(filter),
        )
      : items;
    return {
      chapters: filtered.filter((i) => i.kind === 'chapter'),
      appendices: filtered.filter((i) => i.kind === 'appendix'),
      full: filtered.filter((i) => i.kind === 'full'),
    };
  }, [items, q]);

  const Row = ({ i }: { i: LibraryItem }) => (
    <div className="reading-row">
      <div>
        <div>
          <a
            href={`/api/library/${i.id}/file`}
            target="_blank"
            rel="noreferrer"
          >
            <strong>{i.kind === 'chapter' ? `Ch. ${i.ord_num}` : i.kind === 'appendix' ? `App. ${i.ord}` : 'Full book'}</strong>
            {' — '}{i.title}
          </a>
        </div>
        <div className="muted">
          {i.file_name} {i.size ? `· ${fmtSize(i.size)}` : ''}
          {i.source_url && (
            <>
              {' · '}
              <a href={i.source_url} target="_blank" rel="noreferrer">source</a>
            </>
          )}
        </div>
      </div>
      <a className="btn secondary" href={`/api/library/${i.id}/file`} target="_blank" rel="noreferrer">
        Open
      </a>
    </div>
  );

  return (
    <div>
      <div className="flex-between">
        <div>
          <h2 style={{ margin: 0 }}>Library — Speech & Language Processing (Jurafsky & Martin, 3rd ed.)</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Mirrored from{' '}
            <a href="https://web.stanford.edu/~jurafsky/slp3/" target="_blank" rel="noreferrer">
              web.stanford.edu/~jurafsky/slp3
            </a>{' '}
            for personal study.
          </div>
        </div>
        <input
          type="text"
          placeholder="filter…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 220 }}
        />
      </div>

      {groups.full.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Full PDF</h3>
          {groups.full.map((i) => (
            <Row key={i.id} i={i} />
          ))}
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Chapters ({groups.chapters.length})</h3>
        {groups.chapters.map((i) => (
          <Row key={i.id} i={i} />
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Appendices ({groups.appendices.length})</h3>
        {groups.appendices.map((i) => (
          <Row key={i.id} i={i} />
        ))}
      </div>

      {!items.length && <div className="muted">Loading library…</div>}
    </div>
  );
}
